import React, { Suspense } from "react";
import Layout from "./components/Layout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Login from "./components/auth/login";
import {
  Outlet,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { AuthGate } from "./auth/components/auth-gate";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import BlogsManagements from "./components/seo/blogs/BlogsManagements";
import HandpickedProperties from "./components/selected/HandpickedProperties";
import PropertyGroup from "./components/selected/propertyGroup";
import Users from "./components/users/Users";

// Lazy load all heavy components
const Agencies = React.lazy(() => import("./components/orgs/Organizations"));
const Agency = React.lazy(() => import("./components/orgs/Organization"));
const MapTools = React.lazy(() => import("./components/map/MapTools"));
const PlaceProposalReview = React.lazy(() => import("./components/map/PlaceProposalReview"));
const DataQualityQueue = React.lazy(() => import("./components/data-quality/DataQualityQueue"));
const Properties = React.lazy(
  () => import("./components/properties/Properties")
);
const PropertyManagment = React.lazy(
  () => import("./components/properties/PropertyManagment")
);
const Property = React.lazy(() => import("./components/properties/Property"));

const SlugManagement = React.lazy(
  () => import("./components/seo/slug/SlugManagement")
);
const CityManagement = React.lazy(
  () => import("./components/seo/cities/CityManagement")
);

const ReportsManagement = React.lazy(
  () => import("./components/ReportsManagment/ReportsManagment")
);
const BlogManagement = React.lazy(
  () => import("./components/blog/BlogManagement")
);
const FeaturedProperties = React.lazy(
  () => import("./components/featured/FeaturedProperties")
);
const UserNotifications = React.lazy(
  () => import("./components/user-notifications/UserNotifications")
);
const SponsoredProperties = React.lazy(
  () => import("./components/sponsored-properties/SponsoredProperties")
);
const OverviewDashboard = React.lazy(
  () => import("./components/dashboard/overview-dashboard/OverviewDashboard")
);

const PropertyAnalytics = React.lazy(
  () => import("./components/dashboard/property-analytics/PropertyAnalytics")
);

const UserEngagement = React.lazy(
  () => import("./components/dashboard/user-engagement/UserEngagement")
);

const Reconciliation = React.lazy(
  () => import("./components/dashboard/reconciliation/Reconciliation")
);

const PropertyEngagement = React.lazy(
  () => import("./components/property-engagement/PropertyEngagement")
);
const ControlCenter = React.lazy(
  () => import("./components/control-center/ControlCenter")
);
const Enquiries = React.lazy(() => import("./components/enquiries/Enquiries"));
const WhatsappLeads = React.lazy(
  () => import("./components/whatsapp-leads/WhatsappLeads")
);

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
  </div>
);

function App() {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <Router>
          <Routes>
          <Route
            path="/"
            element={
              <AuthGate>
                <Layout>
                  <Suspense fallback={<LoadingSpinner />}>
                    <Outlet />
                  </Suspense>
                </Layout>
              </AuthGate>
            }>
            <Route path="/agencies" element={<Agencies />} />
            <Route path="/agencies/:id" element={<Agency />} />
            <Route path="/users" element={<Users />} />
            <Route path="/map-tools" element={<MapTools />} />
            <Route path="/place-proposals" element={<PlaceProposalReview />} />
            <Route path="/data-quality" element={<DataQualityQueue />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/property-managment" element={<PropertyManagment />} />
            <Route path="/property/:id" element={<Property />} />
            <Route path="/slug-management" element={<SlugManagement />} />
            <Route path="/cities-management" element={<CityManagement />} />
            <Route path="/reports-management" element={<ReportsManagement />} />
            <Route path="/blog" element={<BlogManagement />} />
            <Route path="/blogs-management" element={<BlogsManagements />} />
            <Route path="/dashboard/dashboard-overview" element={<OverviewDashboard />} />
            <Route path="/dashboard/property-analytics" element={<PropertyAnalytics />} />
            <Route path="/dashboard/user-engagement" element={<UserEngagement />} />
            <Route path="/dashboard/reconciliation" element={<Reconciliation />} />
            <Route path="/dashboard/property-engagement" element={<PropertyEngagement />} />
            <Route path="/control-center" element={<ControlCenter />} />
            <Route path="/enquiries" element={<Enquiries />} />
            <Route path="/whatsapp-leads" element={<WhatsappLeads />} />
            <Route
              path="/featured-properties"
              element={<FeaturedProperties />}
            />
            <Route path="/sponsored-properties" element={<SponsoredProperties />} />
            <Route path="/user-notifications" element={<UserNotifications />} />
            <Route
              path="/handpicked-properties"
              element={<HandpickedProperties />}
            />
            <Route path="/property-group/:id" element={<PropertyGroup />} />
            <Route path="/da/:id" element={<PropertyGroup />} />
          </Route>
          <Route path="/sign-in" element={<Login />} />
        </Routes>
        </Router>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
